import { FireIcon } from '@heroicons/react/outline';
import { ExternalLinkIcon, PaperClipIcon } from '@heroicons/react/solid';
import * as React from 'react';
import {
	InternetAddress,
	NotificationError,
	NotificationRequest,
	NotificationResponse,
} from '@mam/main/Api/Pigeon/Notification';
import { useApiResult } from '@mam/main/ApiHooks';
import { formatUserDateWithWeekday } from '@mam/main/Date';
import { ActivityIndicator, Button, ButtonStyle, Modal, ModalFooter } from '@mam/main/UI';

export const DetailModal: React.FunctionComponent<{
	notificationId: string;
	onClose(): void;
}> = ({ notificationId, onClose }) => {
	const [requestResult] = useApiResult<NotificationRequest, NotificationResponse, NotificationError>(
		new NotificationRequest(notificationId),
		[notificationId]
	);

	const topOffset = React.useMemo(() => window.scrollY, []);

	if (requestResult.value === null) {
		return (
			<Modal topOffset={topOffset}>
				<ActivityIndicator />
				<ModalFooter>
					<button
						className='mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
						type='button'
						onClick={() => {
							onClose();
						}}
					>
						Zavřít
					</button>
				</ModalFooter>
			</Modal>
		);
	}

	const { email } = requestResult.value.notification;
	if (email === null) {
		return (
			<Modal topOffset={topOffset}>
				Notifikace neobsahuje e-mail;
				<ModalFooter>
					<button
						className='mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
						type='button'
						onClick={() => {
							onClose();
						}}
					>
						Zavřít
					</button>
				</ModalFooter>
			</Modal>
		);
	}

	return (
		<Modal
			topOffset={topOffset}
			onClose={() => {
				onClose();
			}}
		>
			<div className='bg-white shadow overflow-hidden sm:rounded-lg'>
				<div className='bg-cogvio-50 px-4 py-5 sm:px-6'>
					<h3 className='text-lg leading-6 font-medium text-gray-900'>{email.subject}</h3>
					<p className='mt-1 max-w-2xl text-sm text-gray-500'>
						{formatUserDateWithWeekday(requestResult.value.notification.createdTime, ['cs'])}
					</p>
				</div>
				<div className='border-t border-gray-200'>
					<dl>
						<DetailRow title='Odesílatel'>
							<InternetAddressHandle address={email.from} />
						</DetailRow>
						<DetailRow title='Příjemce'>
							{email.to.map((recipient, id) => (
								<InternetAddressHandle address={recipient} key={id} />
							))}
						</DetailRow>
						{email.cc.length > 0 ? (
							<DetailRow title='Cc'>
								{email.cc.map((recipient, id) => (
									<InternetAddressHandle address={recipient} key={id} />
								))}
							</DetailRow>
						) : null}
						{email.attachments.length > 0 ? (
							<DetailRow title='Přílohy'>
								<ul className='border border-gray-200 rounded-md divide-y divide-gray-200' role='list'>
									{email.attachments.map((attachment) => (
										<li className='pr-4 py-3 flex items-center justify-between text-sm'>
											<div className='w-0 flex-1 flex items-center'>
												<PaperClipIcon aria-hidden='true' className='flex-shrink-0 h-5 w-5 text-gray-400' />
												<span className='ml-2 flex-1 w-0 truncate'>{attachment.fileName}</span>
											</div>
											<div className='ml-4 flex-shrink-0'>
												<a
													className='font-medium text-cogvio-900 hover:text-cogvioBlue-500 hover:underline'
													href={`/notifications/${notificationId}/emails/${email.id}/attachments/${attachment.id}/download`}
													target='_blank'
												>
													Stáhnout
												</a>
											</div>
										</li>
									))}
								</ul>
							</DetailRow>
						) : null}
					</dl>
				</div>
			</div>
			<ModalFooter>
				<a
					className='mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-cogvio-900 text-white text-base font-medium
							transition duration-200 ease-in-out hover:bg-cogvioBlue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
					href={`/notifications/email/detail?${new URLSearchParams([
						['notificationId', notificationId],
						['emailNotificationId', email.id],
					]).toString()}`}
					target='_blank'
				>
					Zobrazit obsah
					<ExternalLinkIcon className='ml-2 h-5 w-5 text-gray-200' />
				</a>
				<a
					className='mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-red-700 text-white text-base font-medium
							transition duration-200 ease-in-out hover:bg-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
				>
					Nastavit jako důležité
					<FireIcon className='ml-2 h-5 w-5 text-gray-200' />
				</a>
				<Button
					style={ButtonStyle.Cancel}
					onClick={() => {
						onClose();
					}}
				>
					Zavřít
				</Button>
			</ModalFooter>
		</Modal>
	);
};

DetailModal.displayName = 'Application.Notification.DetailModal';

export const DetailRow: React.FunctionComponent<{
	title: string;
}> = ({ children, title }) => (
	<div className='even:bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
		<dt className='text-sm font-medium text-gray-500'>{title}</dt>
		<dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{children}</dd>
	</div>
);

DetailRow.displayName = 'Application.Notification.DetailRow';

export const InternetAddressHandle: React.FunctionComponent<{
	address: InternetAddress;
}> = ({ address }) => (
	<div>{address.personal !== null ? `${address.personal} <${address.emailAddress}>` : `${address.emailAddress}`}</div>
);

InternetAddressHandle.displayName = 'Application.Notification.InternetAddressHandle';
